/**
 * 통화 관련 유틸리티
 * 다통화 지원 기능을 위한 환율 변환 및 포맷팅 함수
 */

// 지원 통화 목록
export const CURRENCY_OPTIONS = [
    { code: 'EUR', name: '유로', symbol: '€', nameEn: 'Euro', nameFr: 'Euro' },
    { code: 'USD', name: '미국 달러', symbol: '$', nameEn: 'US Dollar', nameFr: 'Dollar américain' },
    { code: 'GBP', name: '영국 파운드', symbol: '£', nameEn: 'British Pound', nameFr: 'Livre sterling' },
    { code: 'JPY', name: '일본 엔', symbol: '¥', nameEn: 'Japanese Yen', nameFr: 'Yen japonais' },
    { code: 'KRW', name: '한국 원', symbol: '₩', nameEn: 'Korean Won', nameFr: 'Won coréen' },
    { code: 'CNY', name: '중국 위안', symbol: '¥', nameEn: 'Chinese Yuan', nameFr: 'Yuan chinois' },
    { code: 'PHP', name: '필리핀 페소', symbol: '₱', nameEn: 'Philippine Peso', nameFr: 'Peso philippin' },
    { code: 'THB', name: '태국 바트', symbol: '฿', nameEn: 'Thai Baht', nameFr: 'Baht thaïlandais' },
    { code: 'TWD', name: '대만 달러', symbol: 'NT$', nameEn: 'Taiwan Dollar', nameFr: 'Dollar taïwanais' },
    { code: 'SGD', name: '싱가포르 달러', symbol: 'S$', nameEn: 'Singapore Dollar', nameFr: 'Dollar de Singapour' },
    { code: 'AUD', name: '호주 달러', symbol: 'A$', nameEn: 'Australian Dollar', nameFr: 'Dollar australien' },
    { code: 'CAD', name: '캐나다 달러', symbol: 'C$', nameEn: 'Canadian Dollar', nameFr: 'Dollar canadien' },
    { code: 'CHF', name: '스위스 프랑', symbol: 'CHF', nameEn: 'Swiss Franc', nameFr: 'Franc suisse' },
    { code: 'MXN', name: '멕시코 페소', symbol: 'MX$', nameEn: 'Mexican Peso', nameFr: 'Peso mexicain' },
    { code: 'BRL', name: '브라질 헤알', symbol: 'R$', nameEn: 'Brazilian Real', nameFr: 'Réal brésilien' },
];

// 환율 캐시 (메모리)
let exchangeRatesCache = null;
let lastFetchTime = null;
const CACHE_DURATION = 60 * 60 * 1000; // 1시간

// 기본 환율 (ECB API 실패 시 사용하는 대략적인 값)
const DEFAULT_RATES = {
    EUR: 1,
    USD: 1.08,
    GBP: 0.85,
    JPY: 162,
    KRW: 1450,
    CNY: 7.8,
    PHP: 62,
    THB: 38,
    TWD: 34,
    SGD: 1.45,
    AUD: 1.65,
    CAD: 1.48,
    CHF: 0.94,
    MXN: 18.5,
    BRL: 5.4,
};

/**
 * ECB에서 환율 데이터를 가져옵니다
 * @returns {Promise<Object>} EUR 기준 환율 객체
 */
export async function fetchExchangeRates() {
    // 캐시가 유효하면 반환
    if (exchangeRatesCache && lastFetchTime && (Date.now() - lastFetchTime < CACHE_DURATION)) {
        return exchangeRatesCache;
    }

    try {
        // 백엔드 프록시 API 사용 (CORS 우회)
        // 백엔드 구현 전에는 기본값 사용
        const response = await fetch('/api/exchange-rates');

        if (!response.ok) {
            throw new Error('Exchange rates API failed');
        }

        const data = await response.json();
        if (data.success && data.rates) {
            exchangeRatesCache = { EUR: 1, ...data.rates };
            lastFetchTime = Date.now();
            return exchangeRatesCache;
        }

        throw new Error('Invalid response format');
    } catch (error) {
        console.warn('[currency] 환율 API 호출 실패, 기본값 사용:', error.message);
        // 기본값 반환
        return DEFAULT_RATES;
    }
}

/**
 * 금액을 유로로 환산합니다
 * @param {number} amount - 금액
 * @param {string} currency - 통화 코드 (예: 'USD', 'PHP')
 * @param {Object} rates - 환율 객체 (EUR 기준)
 * @returns {number} 유로 환산 금액
 */
export function convertToEur(amount, currency, rates = DEFAULT_RATES) {
    if (!amount || amount === 0) return 0;
    if (currency === 'EUR') return amount;

    const rate = rates[currency];
    if (!rate) {
        console.warn(`[currency] Unknown currency: ${currency}`);
        return amount; // 알 수 없는 통화면 원래 금액 반환
    }

    return amount / rate;
}

/**
 * 금액을 통화 기호와 함께 포맷팅합니다
 * @param {number} amount - 금액
 * @param {string} currencyCode - 통화 코드
 * @param {boolean} showSymbol - 기호 표시 여부 (기본: true)
 * @returns {string} 포맷팅된 문자열
 */
export function formatCurrency(amount, currencyCode = 'EUR', showSymbol = true) {
    if (amount === null || amount === undefined || amount === '') return '';

    const currency = CURRENCY_OPTIONS.find(c => c.code === currencyCode);
    const symbol = currency?.symbol || currencyCode;

    // 소수점 없는 통화 (JPY, KRW)
    const noDecimalCurrencies = ['JPY', 'KRW', 'TWD'];
    const decimals = noDecimalCurrencies.includes(currencyCode) ? 0 : 2;

    const formattedAmount = Number(amount).toLocaleString('fr-FR', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    });

    if (!showSymbol) return formattedAmount;

    // EUR는 뒤에 기호, 나머지는 앞에 기호
    if (currencyCode === 'EUR') {
        return `${formattedAmount} €`;
    }
    return `${symbol}${formattedAmount}`;
}

/**
 * 통화 코드로 통화 정보를 가져옵니다
 * @param {string} code - 통화 코드
 * @returns {Object|null} 통화 정보 객체
 */
export function getCurrencyInfo(code) {
    return CURRENCY_OPTIONS.find(c => c.code === code) || null;
}

/**
 * 다국어 통화명을 가져옵니다
 * @param {string} code - 통화 코드
 * @param {string} lang - 언어 코드 ('ko', 'en', 'fr')
 * @returns {string} 통화명
 */
export function getCurrencyName(code, lang = 'ko') {
    const currency = getCurrencyInfo(code);
    if (!currency) return code;

    switch (lang) {
        case 'ko': return currency.name;
        case 'en': return currency.nameEn;
        case 'fr': return currency.nameFr;
        default: return currency.nameEn;
    }
}
